import React, { useState, useEffect } from "react";
import {
  Card, CardContent, Typography, Grid, Box, LinearProgress, Divider,
  Chip, Stack, Paper, Tooltip, CircularProgress, useTheme
} from "@mui/material";
import axios from "axios";
import utils from "../../utils";


const Dashboard = () => {
  const theme = useTheme();
  const [data, setData] = useState({
    aziende: 0,
    avvisi: 0,
    fatture: 0,
    pratiche: 0,
    collaborazioni: 0,
    avvisiPagati: 0,
    avvisiDaPagare: 0,
    scaduti: 0,
  });
  const [loading, setLoading] = useState(true);
  const [paymentPercentage, setPaymentPercentage] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Load aziende
      const aziendeRes = await axios.get(`/api/aziendeeop`, {
        headers: utils.getAuthHeaders(),
      });
      const { success: aziendeSuccess, aziende } = aziendeRes.data;
      if (aziendeSuccess) {
        setData(prevData => ({ ...prevData, aziende: aziende.length }));
      }

      // Load fatture
      const fattureRes = await axios.get(`/api/fattureop`, {
        headers: utils.getAuthHeaders(),
      });
      const { success: fattureSuccess, fatture } = fattureRes.data;
      if (fattureSuccess) {
        setData(prevData => ({ ...prevData, fatture: fatture.length }));
      }

      // Load pratiche
      const praticheRes = await axios.get(`/api/ticketsfatti`, {
        headers: utils.getAuthHeaders(),
      });
      const { success: praticheSuccess, total } = praticheRes.data;
      if (praticheSuccess) {
        setData(prevData => ({ ...prevData, pratiche: total }));
      }

      // Load collaborazioni
      const collaborazioniRes = await axios.get(`/api/collaborazioni`, {
        headers: utils.getAuthHeaders(),
      });
      const { success: collaborazioniSuccess, clienti } = collaborazioniRes.data;
      if (collaborazioniSuccess) {
        setData(prevData => ({ ...prevData, collaborazioni: clienti.length }));
      }

      // Load avvisi
      const avvisiPagatiRes = await axios.get(`/api/avvisiparcellaop?y=all`, {
        headers: utils.getAuthHeaders(),
      });
      const { success: avvisiPagatiSuccess, avvisi } = avvisiPagatiRes.data;
      if (avvisiPagatiSuccess) {
        setData(prevData => ({
          ...prevData,
          avvisi: avvisi.length,
          avvisiPagati: avvisi.filter(a => a.pagato).length,
          avvisiDaPagare: avvisi.filter(a => !a.pagato).length,
          scaduti: avvisi.filter(a => !a.pagato && Date.now() - a.confermato_at > 30 * 24 * 60 * 60 * 1000).length
        }));

        // Calculate payment percentage
        const totalAvvisi = (avvisi.filter(a => !a.compensato).reduce((tot, avviso) => tot + avviso.tot_a_pagare, 0) / 100).toFixed(2);
        const pagati = (avvisi.filter(a => !a.compensato && a.pagato).reduce((tot, avviso) => tot + avviso.tot_a_pagare, 0) / 100).toFixed(2);
        const percentage = totalAvvisi > 0 ? (pagati / totalAvvisi) * 100 : 0;
        setPaymentPercentage(percentage);
      }


    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  const renderStatCard = (title, value) => {

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: '16px',
          boxShadow: 3,
          transition: '0.3s',
          height: '100%',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-4px)'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>{value}</Typography>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Caricamento dati...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%' }}>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Aziende", data.aziende)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Fatture", data.fatture)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Pratiche", data.pratiche)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Collaborazioni", data.collaborazioni)}
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Avvisi di Parcella", data.avvisi)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Pagati", data.avvisiPagati)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Da Pagare", data.avvisiDaPagare)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard("Scaduti", data.scaduti)}
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: '16px', boxShadow: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Percentuale Pagamenti</Typography>
                <Chip
                  label={`${Math.round(paymentPercentage)}% completato`}
                  color={paymentPercentage > 75 ? "success" : paymentPercentage > 50 ? "info" : paymentPercentage > 25 ? "warning" : "error"}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={paymentPercentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      bgcolor: paymentPercentage > 75 ? theme.palette.success.main :
                        paymentPercentage > 50 ? theme.palette.info.main :
                          paymentPercentage > 25 ? theme.palette.warning.main :
                            theme.palette.error.main,
                      borderRadius: 5,
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    0%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    100%
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {paymentPercentage > 75 ? "Eccellente! La maggior parte degli avvisi sono stati pagati." :
                    paymentPercentage > 50 ? "Buono. Più della metà degli avvisi è stata pagata." :
                      paymentPercentage > 25 ? "Attenzione: molti avvisi sono ancora da pagare." :
                        "Critico: la maggior parte degli avvisi non è stata pagata."}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;